import React, { useState, useRef } from 'react';
import { Box, VStack, Flex, Icon, Text, Collapse, Tooltip, Portal, useDisclosure, useOutsideClick } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import {
  FaHome,
  FaBox,
  FaUsers,
  FaTruck,
  FaFileInvoice,
  FaBars,
  FaTags,
  FaAngleDown,
  FaAngleUp,
  FaPrint
} from 'react-icons/fa';

const Sidebar = ({ isExpanded, setIsExpanded }) => {
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const { isOpen: isCollapsedMenuOpen, onOpen: onCollapsedMenuOpen, onClose: onCollapsedMenuClose } = useDisclosure();
  const [menuRef, setMenuRef] = useState(null);

  const toggleSidebar = () => setIsExpanded(!isExpanded);
  const toggleInvoiceMenu = () => setIsInvoiceOpen(!isInvoiceOpen);

  // Close collapsed menu when clicking outside
  useOutsideClick({
    ref: { current: menuRef },
    handler: onCollapsedMenuClose,
  });

  return (
    <Box
      bg="primary.500"
      color="white"
      w={isExpanded ? "230px" : "110px"}
      h="100vh"
      pt="5"
      position="fixed"
      top="0"
      left="0"
      zIndex={2} // Ensure sidebar is above background
      transition="width 0.3s ease"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <VStack align="stretch" spacing="0" mt="10">
        <Tooltip label="Головна" placement="right" isDisabled={isExpanded}>
          <Box>
            <NavLink
              to="/"
              style={({ isActive }) => ({
                backgroundColor: isActive ? '#142c4d' : 'transparent',
              })}
            >
              <Box p="5" _hover={{ bg: 'primary.600' }}>
                <Flex align="center">
                  <Icon as={FaHome} boxSize="5" />
                  <Text ml="4" display={isExpanded ? "block" : "none"}>Борда</Text>
                </Flex>
              </Box>
            </NavLink>
          </Box>
        </Tooltip>

        <Tooltip label="Категорії" placement="right" isDisabled={isExpanded}>
          <Box>
            <NavLink to="/categories" style={({ isActive }) => ({ backgroundColor: isActive ? '#142c4d' : 'transparent' })}>
              <Box p="5" _hover={{ bg: 'primary.600' }}>
                <Flex align="center">
                  <Icon as={FaTags} boxSize="5" />
                  <Text ml="4" display={isExpanded ? "block" : "none"}>Категорії</Text>
                </Flex>
              </Box>
            </NavLink>
          </Box>
        </Tooltip>

        <Tooltip label="Товари" placement="right" isDisabled={isExpanded}>
          <Box>
            <NavLink to="/products" style={({ isActive }) => ({ backgroundColor: isActive ? '#142c4d' : 'transparent' })}>
              <Box p="5" _hover={{ bg: 'primary.600' }}>
                <Flex align="center">
                  <Icon as={FaBox} boxSize="5" />
                  <Text ml="4" display={isExpanded ? "block" : "none"}>Товар</Text>
                </Flex>
              </Box>
            </NavLink>
          </Box>
        </Tooltip>

        <Tooltip label="Покупці" placement="right" isDisabled={isExpanded}>
          <Box>
            <NavLink to="/customers" style={({ isActive }) => ({ backgroundColor: isActive ? '#142c4d' : 'transparent' })}>
              <Box p="5" _hover={{ bg: 'primary.600' }}>
                <Flex align="center">
                  <Icon as={FaUsers} boxSize="5" />
                  <Text ml="4" display={isExpanded ? "block" : "none"}>Покупці</Text>
                </Flex>
              </Box>
            </NavLink>
          </Box>
        </Tooltip>

        <Tooltip label="Постачальники" placement="right" isDisabled={isExpanded}>
          <Box>
            <NavLink to="/suppliers" style={({ isActive }) => ({ backgroundColor: isActive ? '#142c4d' : 'transparent' })}>
              <Box p="5" _hover={{ bg: 'primary.600' }}>
                <Flex align="center">
                  <Icon as={FaTruck} boxSize="5" />
                  <Text ml="4" display={isExpanded ? "block" : "none"}>Постачальники</Text>
                </Flex>
              </Box>
            </NavLink>
          </Box>
        </Tooltip>

        <Tooltip label="Накладні" placement="right" isDisabled={isExpanded}>
          <Box 
            p="5" 
            cursor="pointer" 
            onClick={isExpanded ? toggleInvoiceMenu : onCollapsedMenuOpen} 
            _hover={{ bg: 'primary.600' }}
            position="relative"
            ref={setMenuRef}
          >
            <Flex align="center" justify="space-between">
              <Flex align="center">
                <Icon as={FaFileInvoice} boxSize="5" />
                <Text ml="4" display={isExpanded ? 'block' : 'none'}>
                  Накладні
                </Text>
              </Flex>
              <Icon as={isInvoiceOpen ? FaAngleUp : FaAngleDown} display={isExpanded ? 'block' : 'none'} />
            </Flex>

            {/* Collapsed menu overlay */}
            {!isExpanded && isCollapsedMenuOpen && (
              <Box
                position="absolute"
                left="80px"
                top="0"
                bg="white"
                color="gray.800"
                borderRadius="lg"
                boxShadow="xl"
                border="1px solid"
                borderColor="gray.200"
                minW="180px"
                zIndex={1000}
                py={2}
              >
                <Text px={4} py={2} fontWeight="bold" fontSize="sm" color="gray.600" borderBottom="1px solid" borderColor="gray.100">
                  Накладні
                </Text>
                <VStack align="stretch" spacing={0}>
                  <NavLink 
                    to="/sell-invoices"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <Box
                      px={4}
                      py={3}
                      _hover={{ bg: 'gray.50' }}
                      transition="background 0.2s"
                      onClick={(e) => {
                        onCollapsedMenuClose();
                      }}
                      cursor="pointer"
                    >
                      <Text fontSize="sm">📊 Продаж</Text>
                    </Box>
                  </NavLink>
                  <NavLink 
                    to="/arrival-invoices"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <Box
                      px={4}
                      py={3}
                      _hover={{ bg: 'gray.50' }}
                      transition="background 0.2s"
                      onClick={(e) => {
                        onCollapsedMenuClose();
                      }}
                      cursor="pointer"
                    >
                      <Text fontSize="sm">📥 Прихід</Text>
                    </Box>
                  </NavLink>
                  <NavLink 
                    to="/return-invoices"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <Box
                      px={4}
                      py={3}
                      _hover={{ bg: 'gray.50' }}
                      transition="background 0.2s"
                      borderBottomRadius="lg"
                      onClick={(e) => {
                        onCollapsedMenuClose();
                      }}
                      cursor="pointer"
                    >
                      <Text fontSize="sm">↩️ Повернення</Text>
                    </Box>
                  </NavLink>
                </VStack>
              </Box>
            )}
          </Box>
        </Tooltip>
        <Collapse in={isInvoiceOpen} animateOpacity>
          <Box pl="8" display={isExpanded ? 'block' : 'none'}>
            <NavLink to="/sell-invoices" style={({ isActive }) => ({ color: isActive ? '#ffffff' : '#a0aec0' })}>
              <Text py="2" _hover={{ color: '#ffffff' }}>Продаж</Text>
            </NavLink>
            <NavLink to="/arrival-invoices" style={({ isActive }) => ({ color: isActive ? '#ffffff' : '#a0aec0' })}>
              <Text py="2" _hover={{ color: '#ffffff' }}>Прихід</Text>
            </NavLink>
            <NavLink to="/return-invoices" style={({ isActive }) => ({ color: isActive ? '#ffffff' : '#a0aec0' })}>
              <Text py="2" _hover={{ color: '#ffffff' }}>Повернення</Text>
            </NavLink>
          </Box>
        </Collapse>
      </VStack>

      {/* Print product card link at the bottom */}
      <Box>
        <Tooltip label="Друк карток" placement="right" isDisabled={isExpanded}>
          <Box>
            <NavLink
              to="/print-product-cards"
              style={({ isActive }) => ({
                backgroundColor: isActive ? '#142c4d' : 'transparent',
              })}
            >
              <Box p="5" _hover={{ bg: 'primary.600' }}>
                <Flex align="center">
                  <Icon as={FaPrint} boxSize="5" />
                  <Text ml="4" display={isExpanded ? "block" : "none"}>Друк карток</Text>
                </Flex>
              </Box>
            </NavLink>
          </Box>
        </Tooltip>
      </Box>

      {/* Toggle Button */}
      <Tooltip label={isExpanded ? "Згорнути меню" : "Розгорнути меню"} placement="right">
        <Box
          onClick={toggleSidebar}
          position="absolute"
          bottom="20"
          right={isExpanded ? "-15px" : "-20px"}
          bg="primary.600"
          p="2"
          cursor="pointer"
          borderRadius="md"
          _hover={{ bg: "primary.700" }}
          color="white"
        >
          <Icon as={FaBars} />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default Sidebar;